import type { ReactElement, ReactNode } from 'react';
import React from 'react';
import { PopoverBody, PopoverHeader, UncontrolledPopover } from 'reactstrap';
import type { BoxWhiskerData } from './types';

type Props = {
  data: BoxWhiskerData;
  header?: ReactNode;
  referenceData?: BoxWhiskerData;
  id: string;
};

/**
 * Box Whisker Tooltip
 *
 * A tooltip displaying detailed information about the dimensions and data of the Box Whisker
 *
 */

export default function BoxWhiskerTooltip({
  data,
  header = data.label,
  referenceData,
  id
}: Props): ReactElement {
  const highestScore = `${data.maximum?.toFixed(1)}${
    referenceData ? `(${referenceData.maximum?.toFixed(1)})` : ``
  }`;
  const upperQuartile = `${data.upperQuartile?.toFixed(1)}${
    referenceData ? `(${referenceData.upperQuartile?.toFixed(1)})` : ``
  }`;
  const median = `${data.median?.toFixed(1)}${
    referenceData ? `(${referenceData.median?.toFixed(1)})` : ``
  }`;
  const lowerQuartile = `${data.lowerQuartile?.toFixed(1)}${
    referenceData ? `(${referenceData.lowerQuartile?.toFixed(1)})` : ``
  }`;
  const lowestScore = `${data.minimum?.toFixed(1)}${
    referenceData ? `(${referenceData.minimum?.toFixed(1)})` : ``
  }`;

  return (
    <UncontrolledPopover
      trigger="hover focus"
      target={(() => document.getElementById(id)) as unknown as HTMLElement}
    >
      <PopoverHeader>{header}</PopoverHeader>
      <PopoverBody>
        <table className="table table-sm">
          <thead>
            <tr>
              <th scope="col" aria-label="Statistics" />
              <th className="text-end" scope="col">
                {referenceData ? 'Value (Ref)' : 'Value'}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <th scope="row">Responses:</th>
              <td className="text-end">{data.numberOfResponses}</td>
            </tr>
            <tr>
              <th scope="row">Highest Score:</th>
              <td className="text-end">{highestScore}</td>
            </tr>
            <tr>
              <th scope="row">Upper Quartile:</th>
              <td className="text-end">{upperQuartile}</td>
            </tr>
            <tr>
              <th scope="row">Median:</th>
              <td className="text-end">{median}</td>
            </tr>
            <tr>
              <th scope="row">Lower Quartile:</th>
              <td className="text-end">{lowerQuartile}</td>
            </tr>
            <tr>
              <th className="text-capitalize" scope="row">
                Lowest Score:
              </th>
              <td className="text-end">{lowestScore}</td>
            </tr>
          </tbody>
        </table>
      </PopoverBody>
    </UncontrolledPopover>
  );
}
